import { Alert, Button, Input, message, Space, Spin, Table, Tabs, Tag, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import React, { useEffect, useState } from 'react';
import PageLayout from '../layouts/PageLayout';
import { fetchReferralTransactions, ReferralTransaction } from '../services/paymentService';
import { fetchCurrentUser, fetchReferrals, ReferralUser } from '../services/userService';

const { Paragraph, Text, Title } = Typography;

const ReferralSettingsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refCode, setRefCode] = useState<string>('');
  const [refId, setRefId] = useState<number | null>(null);
  const [referrals, setReferrals] = useState<ReferralUser[]>([]);
  const [referralTransactions, setReferralTransactions] = useState<ReferralTransaction[]>([]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [user, referralsData, transactionsData] = await Promise.all([
          fetchCurrentUser(),
          fetchReferrals(),
          fetchReferralTransactions(),
        ]);
        setRefCode(user.refCode);
        setRefId(user.refId ?? null);
        setReferrals(referralsData);
        setReferralTransactions(transactionsData);
        setError(null);
      } catch (err: any) {
        setError(err?.message || 'Failed to load referral info');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const cabinetRefLink = refCode ? `${window.location.origin}?fsrc=${refCode}` : '';
  const landingRefLink = refCode ? `https://flipscout.fun?fsrc=${refCode}` : '';

  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      message.success('Copied to clipboard');
    } catch {
      message.error('Failed to copy');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatAmount = (value: string): string => {
    if (!value) return '0';
    const normalized = value.trim();
    if (!normalized) return '0';

    const [integerPart = '0', fractionalPart] = normalized.split('.');
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    if (fractionalPart === undefined) {
      return formattedInteger;
    }

    const trimmedFractional = fractionalPart.replace(/0+$/, '');
    return trimmedFractional ? `${formattedInteger}.${trimmedFractional}` : formattedInteger;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'green';
      case 'pending':
        return 'blue';
      case 'failed':
        return 'red';
      case 'canceled':
        return 'default';
      default:
        return 'default';
    }
  };

  const columns: ColumnsType<ReferralUser> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (email) => email || '—',
    },
    {
      title: 'Wallet Address',
      dataIndex: 'walletAddress',
      key: 'walletAddress',
      render: (address) => address || '—',
    },
    {
      title: 'Registered',
      dataIndex: 'registeredAt',
      key: 'registeredAt',
      render: (date) => formatDate(date),
    },
  ];

  const transactionColumns: ColumnsType<ReferralTransaction> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Amount',
      key: 'amount',
      render: (_, record) => `${formatAmount(record.amount)} ${record.currency}`,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <Tag color={getStatusColor(status)}>{status.toUpperCase()}</Tag>,
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => formatDate(date),
    },
  ];

  return (
    <PageLayout title="Referrals" backTo="/">
      <Spin spinning={loading}>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {error && <Alert type="error" message={error} showIcon />}

          {!error && (
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <div>
                <Title level={5} style={{ marginBottom: 8 }}>
                  Landing Ref Link
                </Title>
                <Input
                  value={landingRefLink || '—'}
                  readOnly
                  size="large"
                  addonAfter={
                    <Button
                      type="link"
                      disabled={!landingRefLink}
                      onClick={() => handleCopy(landingRefLink)}
                      style={{ height: '100%', display: 'flex', alignItems: 'center' }}
                    >
                      Copy Link
                    </Button>
                  }
                />
              </div>
              <div>
                <Title level={5} style={{ marginBottom: 8 }}>
                  Cabinet Ref Link
                </Title>
                <Input
                  value={cabinetRefLink || '—'}
                  readOnly
                  size="large"
                  addonAfter={
                    <Button
                      type="link"
                      disabled={!cabinetRefLink}
                      onClick={() => handleCopy(cabinetRefLink)}
                      style={{ height: '100%', display: 'flex', alignItems: 'center' }}
                    >
                      Copy Link
                    </Button>
                  }
                />
              </div>

              <Tabs
                items={[
                  {
                    key: 'referrals',
                    label: `Referrals (${referrals.length})`,
                    children: referrals.length > 0 ? (
                      <Table
                        columns={columns}
                        dataSource={referrals}
                        rowKey="id"
                        pagination={{ pageSize: 10 }}
                        size="small"
                      />
                    ) : (
                      <Paragraph type="secondary">No referrals yet. Share your referral link to invite users!</Paragraph>
                    ),
                  },
                  {
                    key: 'transactions',
                    label: `Referral Transactions (${referralTransactions.length})`,
                    children: referralTransactions.length > 0 ? (
                      <Table
                        columns={transactionColumns}
                        dataSource={referralTransactions}
                        rowKey="id"
                        pagination={{ pageSize: 10 }}
                        size="small"
                      />
                    ) : (
                      <Paragraph type="secondary">No referral transactions yet. You'll see commission payments here when your referrals make payments.</Paragraph>
                    ),
                  },
                ]}
              />
            </Space>
          )}
        </Space>
      </Spin>
    </PageLayout>
  );
};

export default ReferralSettingsPage;
